import { useState, useCallback } from 'react'
import { authFetch } from '../utils/authFetch'
import { LiveAttributionWindow, SegmentProcessResult } from '../types'

interface UseSegmentProcessReturn {
  segmentResult: SegmentProcessResult | null
  isLoading: boolean
  error: string | null
  startSegmentProcess: (options: {
    segment: string
    startDate?: string
    endDate?: string
    forceDownload?: boolean
    forceProcess?: boolean
    filterField?: 'segment' | 'planType'
    liveAttributionWindow?: LiveAttributionWindow
  }) => Promise<{ sessionId: string }>
  startGroupProcess: (options: {
    companyKeys: string[]
    startDate?: string
    endDate?: string
    forceDownload?: boolean
    forceProcess?: boolean
    liveAttributionWindow?: LiveAttributionWindow
  }) => Promise<{ sessionId: string }>
  clearSegmentResult: () => void
}

export function useSegmentProcess(): UseSegmentProcessReturn {
  const [segmentResult, setSegmentResult] = useState<SegmentProcessResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startSegmentProcess = useCallback(async (options: {
    segment: string
    startDate?: string
    endDate?: string
    forceDownload?: boolean
    forceProcess?: boolean
    filterField?: 'segment' | 'planType'
    liveAttributionWindow?: LiveAttributionWindow
  }): Promise<{ sessionId: string }> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch('/api/process-segment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Erro ao iniciar processamento de segmento')
      }

      const data = await response.json()

      if (!data.success || !data.sessionId) {
        throw new Error('Resposta invalida do servidor')
      }

      return { sessionId: data.sessionId }
    } catch (err: any) {
      console.error('Erro ao iniciar processamento de segmento:', err)
      setError(err.message || 'Erro ao iniciar processamento de segmento')
      setIsLoading(false)
      throw err
    }
  }, [])

  const startGroupProcess = useCallback(async (options: {
    companyKeys: string[]
    startDate?: string
    endDate?: string
    forceDownload?: boolean
    forceProcess?: boolean
    liveAttributionWindow?: LiveAttributionWindow
  }): Promise<{ sessionId: string }> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch('/api/process-segment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Erro ao iniciar processamento de grupo')
      }

      const data = await response.json()

      if (!data.success || !data.sessionId) {
        throw new Error('Resposta invalida do servidor')
      }

      return { sessionId: data.sessionId }
    } catch (err: any) {
      console.error('Erro ao iniciar processamento de grupo:', err)
      setError(err.message || 'Erro ao iniciar processamento de grupo')
      setIsLoading(false)
      throw err
    }
  }, [])

  const clearSegmentResult = useCallback(() => {
    setSegmentResult(null)
    setError(null)
  }, [])

  return {
    segmentResult,
    isLoading,
    error,
    startSegmentProcess,
    startGroupProcess,
    clearSegmentResult,
  }
}
